/**
 * Workout use cases – submit lift (with optional video or pre-uploaded videoUrl), list feed, list by user.
 * removeWorkoutVideo: delete video from a lift (owner only).
 */
import { workoutRepo } from '../../data/repositories/WorkoutRepository';
import { uploadLiftVideo, deleteLiftVideoByUrl } from '../../data/firebase/storage';
import { getResetWorkoutVerificationsCallable } from '../../data/firebase/config';
import { userRepo } from '../../data/repositories/UserRepository';

export async function submitLift(userId, { exercise, weight, reps, videoFile, videoUrl: preUploadedUrl, showInFeed = false }) {
  let videoUrl = preUploadedUrl || '';
  if (videoFile && !preUploadedUrl) {
    videoUrl = await uploadLiftVideo(userId, videoFile);
  }
  const user = await userRepo.getById(userId);
  const stats = user?.stats ?? {};
  const exerciseKey = exercise.toLowerCase().replace(/\s/g, '');
  const prevBest = stats[`${exerciseKey}Pr`] ?? stats[exercise] ?? 0;
  const isPR = Number(weight) > Number(prevBest);
  const workoutId = await workoutRepo.create({
    userId,
    exercise,
    weight: Number(weight),
    reps: Number(reps) || 1,
    videoUrl,
    isPR,
    verified: false,
    discipline: user?.discipline || '',
    showInFeed: showInFeed === true,
  });
  if (showInFeed) {
    await workoutRepo.setUserFeedWorkout(userId, workoutId);
  }
  if (isPR && user) {
    await userRepo.set(userId, {
      stats: { ...stats, [exerciseKey]: Number(weight), [`${exerciseKey}Pr`]: Number(weight) },
    });
  }
  return workoutId;
}

/** Remove video from a workout (delete file in Storage, clear videoUrl). Caller must ensure current user is owner. */
export async function removeWorkoutVideo(workoutId, userId) {
  const workout = await workoutRepo.getById(workoutId);
  if (!workout || workout.userId !== userId) throw new Error('Not allowed');
  if (workout.videoUrl) {
    await deleteLiftVideoByUrl(workout.videoUrl);
    await workoutRepo.update(workoutId, { videoUrl: '' });
  }
}

/** Delete entire workout (and its video in Storage). Caller must be owner. */
export async function deleteWorkout(workoutId, userId) {
  const workout = await workoutRepo.getById(workoutId);
  if (!workout || workout.userId !== userId) throw new Error('Not allowed');
  if (workout.videoUrl) {
    try {
      await deleteLiftVideoByUrl(workout.videoUrl);
    } catch (e) {
      console.warn('Could not delete video file', e);
    }
  }
  await workoutRepo.delete(workoutId);
}

export function subscribeFeed(limitCount, onUpdate) {
  return workoutRepo.subscribeFeed(limitCount, onUpdate);
}

/** Realtime feed with optional filters: { exercise, discipline }. */
export function subscribeFeedFiltered(limitCount, filters, onUpdate) {
  return workoutRepo.subscribeFeedFiltered(limitCount, filters, onUpdate);
}

export async function getFeedPage(limitCount, afterDoc) {
  return workoutRepo.listFeed(limitCount, afterDoc);
}

export function subscribeUserWorkouts(userId, limitCount, onUpdate) {
  return workoutRepo.subscribeByUser(userId, limitCount, onUpdate);
}

export async function getUserWorkouts(userId, limitCount = 50) {
  return workoutRepo.listByUser(userId, limitCount);
}

/** Set the single workout that appears in the feed for this user. */
export async function setUserFeedWorkout(userId, workoutId) {
  return workoutRepo.setUserFeedWorkout(userId, workoutId);
}

/** Reset verification count to 0/20 for a workout (owner only, via callable). Call after setting feed so the lift can be re-verified. */
export async function resetWorkoutVerifications(workoutId) {
  const callable = getResetWorkoutVerificationsCallable();
  await callable({ workoutId });
}
